// 22.01.19 - Specifies the actions used by the form_wrapper and view cases.
import {
  all,
  call,
  takeLatest,
  put,
  takeEvery,
  select,
  delay,
} from "redux-saga/effects";
import { reset } from "redux-form";
import { ActionTypes } from "../constants/actions";
import { SEPARATOR, CURRENCY_LIST } from "../constants/data";
import {
  updateCaseUtility,
  saveCaseUtility,
  saveAttachmentDataUtility,
  downloadAttachmentUtility,
  deleteAttachmentUtility,
  caseLockUnlockUtility,
  caseLockForEditUtility,
  requestForCloseUtility,
  apiSaveSPUserData,
} from "src/effects/api/detail";
import { history } from "src/store/config";
import {
  saveCaseSucceeded,
  saveCaseFailed,
  fetchCaseSucceeded,
  actionSetAttachmentData,
  actionSetAttachmentDataShipment,
  actionSetAttachmentDataShipmentInventory,
  actionSetAttachmentDataQmu,
  actionSetAttachmentDataInventory,
  actionSetAttachmentDataQmuOer,
  newCase,
  allowEditCase,
  caseLockForEdit,
  isLockForEdit,
  navigateHome,
  actionSetCurrentUsername,
  actionIsCheckOutByDiffUser,
  actionIsCurrentUserSO,
  actionIsCurrentUserST,

  // fetchCase as actionFetchCase
} from "src/actions/detail";
import {
  setLoaderVisibility,
  openToaster,
  actionAllowFetchGridData,
  closeConfirmDialog,
  closeToaster,
} from "src/actions/common";
import { getRequest, getRequestSync } from "src/effects/api/common";
import {
  GET_CASE,
  DETAIL_PATH,
  HOME_PATH,
  // INTRO_PATH,
  PRINT_PATH,
  // ICase,
  CaseType,
  ClaimStatus,
  UserRoles,
  IssueType,
  CaseFormType,
} from "src/constants/api";
import { IRootState } from "src/reducers";
import { API_ERROR_MESSAGE, FILE_DOWNLOAD_ERROR } from "src/constants/error";
import {
  base64ToArrayBuffer,
  emptyDate,
  getStringBeforeSubstring,
  getStringAfterSubstring,
} from "src/utils";
import {
  FORM_TYPE_MAIN,
  FORM_TYPE_INVENTORY,
  FORM_TYPE_QMU,
  FORM_TYPE_SHIPMENT,
  FORM_TYPE_SHIPMENT_INVENTORY,
  FORM_TYPE_QMU_OER,
  REOPEN_MESSAGE,
  CDK_APPROVE,
  CDK_CLOSE_BTN,
  REQUEST_FOR_CLOSE_MESSAGE,
  DATA_SAVE_SUCCESS,
} from "src/constants/data.js";
import { solutionCentreList } from "../constants/api";
import { responsibilityData } from "../constants/dropdown";
import { constants } from "buffer";

function* watchUpdateCase() {
  yield takeLatest(ActionTypes.DETAIL.UPDATE_CASE, updateCase);
}

function* watchSaveCase() {
  yield takeLatest(ActionTypes.DETAIL.SAVE_CASE, saveCase);
}

function* watchCaseSaveSucceded() {
  yield takeLatest(ActionTypes.DETAIL.SAVE_CASE_SUCCEEDED, navigate(HOME_PATH));
}

function* watchNavigateHome() {
  yield takeLatest(ActionTypes.DETAIL.NAVIGATE_HOME, navigate(HOME_PATH));
}

function* watchNavigatePrint() {
  yield takeLatest(ActionTypes.DETAIL.NAVIGATE_PRINT, navigate(PRINT_PATH));
}

function navigate(to: string) {
  return function* () {
    yield call([history, "push"], to);
  };
}

function* watchFetchCase() {
  yield takeEvery(ActionTypes.DETAIL.FETCH_CASE, fetchCase);
}

function* watchSaveAttachment() {
  yield takeLatest(ActionTypes.DETAIL.SAVE_ATTACHMENT_DATA, saveAttachment);
}

function* watchFileDownload() {
  yield takeLatest(ActionTypes.DETAIL.DOWNLOAD_ATTACHMENT, fileDownload);
}

function* watchFileDelete() {
  yield takeLatest(ActionTypes.DETAIL.DELETE_ATTACHMENT, fileDelete);
}

function* watchCaseLockUnlock() {
  yield takeLatest(ActionTypes.DETAIL.CASE_LOCK_UNLOCK, fnCaseLockUnlock);
}

function* watchCaseLockForEdit() {
  yield takeLatest(ActionTypes.DETAIL.CASE_LOCK_FOR_EDIT, fnCaseLockForEdit);
}

function* watchConfirmDialog() {
  yield takeLatest(ActionTypes.COMMON.CONFIRM_DIALOG_OK, fnConfirmDialog);
}

function* watchRequestForClose() {
  yield takeLatest(ActionTypes.DETAIL.REQUEST_FOR_CLOSE, fnRequestForClose);
}

function* fetchCase(action: any) {
  const res = yield call(getRequestSync, [GET_CASE, action.payload]);
  const state: IRootState = yield select();
  if (res.success) {
    const {
      insurancePolicyYear,
      qmus,
      shipments,
      expectedLossValue,
      pos,
    } = res.data.cases;
    const { attachments, zpox2 } = res.data;
    yield put(setLoaderVisibility(true));

    // Remove the spaces before and after (if any) appearing in the brackets of username
    const createdBy = res.data.cases.createdBy;
    let tmpEmail = getStringAfterSubstring(createdBy, "(");
    tmpEmail = getStringBeforeSubstring(tmpEmail, ")").trim();
    res.data.cases.createdBy = `${getStringBeforeSubstring(
      createdBy,
      "("
    )}(${tmpEmail})`;

    // Check issue type
    if (res.data.cases.issueType !== IssueType.Shipment && res.data.cases.issueType <= IssueType.Pharmacovigilance) {
      res.data.cases.inCaseOfShipment = "";
    }

    if (insurancePolicyYear) {
      res.data.cases.insurancePolicyYear = new Date(insurancePolicyYear)
        .getFullYear()
        .toString(); // update/change the insurance date to year(only year needs to be shown) format
    }
    if (res.data.cases.countriesAffected) {
      setTimeout(() => { }, 100);
      const countriesData = state.detail.countriesData;
      const countriesAffected = res.data.cases.countriesAffected.split(",");
      res.data.cases.countriesAffected = countriesData
        .filter((item: any) => countriesAffected.includes(item.id))
        .map((item: any) => {
          return {
            ...item,
            label: item.countryName,
            value: item.id,
          };
        });

      res.data.cases.regions = res.data.cases.countriesAffected
        .map((item: any) => item.unicefregion)
        .toString();
    }

    if (expectedLossValue) {
      const currentData = expectedLossValue.split(SEPARATOR);
      res.data.cases.expectedLossValue = currentData[1];
      res.data.cases.expectedLossCurrency = currentData[0];
    } else if (res.data.cases.caseFormType !== CaseFormType.WarehouseNonConformity) {
      res.data.cases.expectedLossCurrency = CURRENCY_LIST[0].value;
    }

    // Update the isLockForEdit case value. This value is used to check if a case is approved or not.
    if (res.data.cases.isLockedForEdit) {
      res.data.cases.isLockedForEdit = true;
    } else {
      res.data.cases.isLockedForEdit = false;
    }
    yield put(isLockForEdit(res.data.cases.isLockedForEdit));
    yield put(fetchCaseSucceeded(res.data.cases));
    yield put(setLoaderVisibility(false));

    // Populate PO Data form vision
    if (zpox2.length) {
      const newPosData = pos.map((item: any, index: any) => {
        const poItemData = zpox2
          .filter((poitem: any) => {
            if (poitem.pono === item.poNo) {
              return poitem;
            }
          })
          .filter((poitem: any) => {
            if (Number(poitem.poitem) === Number(item.poItemNo)) {
              return poitem;
            }
          });
        if (poItemData.length) {
          return {
            ...item,
            materialShortText: poItemData[0].materialShortText,
            materialGroup: poItemData[0].materialGroup,
            vendorNo: poItemData[0].vendorCode,
            vendorName: poItemData[0].vendorName,
            freightForwarderNo: poItemData[0].forwarderCode,
            freightForwarderName: poItemData[0].forwarderName,
            orderType: poItemData[0].potype,
            soldToParty: poItemData[0].soldToPartyName,
          };
        } else {
          return {
            ...item,
          };
        }
      });
      res.data.cases.pos = newPosData;
    }

    if (shipments[0]) {
      if (shipments[0].actualInsuredValues) {
        const freightCurrencyData = shipments[0].actualInsuredValues.split(
          SEPARATOR
        );
        shipments[0].actualInsuredValues = freightCurrencyData[1];
        shipments[0].actualInsuredValuesCurrency = freightCurrencyData[0];
      } else {
        shipments[0].actualInsuredValuesCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].premium) {
        const freightCurrencyData = shipments[0].premium.split(SEPARATOR);
        shipments[0].premium = freightCurrencyData[1];
        shipments[0].premiumCurrency = freightCurrencyData[0];
      } else {
        shipments[0].premiumCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].totalClaimsValuesSupplies) {
        const freightCurrencyData = shipments[0].totalClaimsValuesSupplies.split(
          SEPARATOR
        );
        shipments[0].totalClaimsValuesSupplies = freightCurrencyData[1];
        shipments[0].totalClaimsValuesSuppliesCurrency = freightCurrencyData[0];
      } else {
        shipments[0].totalClaimsValuesSuppliesCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].claimsValueFreight) {
        const freightCurrencyData = shipments[0].claimsValueFreight.split(
          SEPARATOR
        );
        shipments[0].claimsValueFreight = freightCurrencyData[1];
        shipments[0].claimsValueFreightCurrency = freightCurrencyData[0];
      } else {
        shipments[0].claimsValueFreightCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].uplift) {
        const freightCurrencyData = shipments[0].uplift.split(SEPARATOR);
        shipments[0].uplift = freightCurrencyData[1];
        shipments[0].upliftCurrency = freightCurrencyData[0];
      } else {
        shipments[0].upliftCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].deductibleUnderThePolicy) {
        const freightCurrencyData = shipments[0].deductibleUnderThePolicy.split(
          SEPARATOR
        );
        shipments[0].deductibleUnderThePolicy = freightCurrencyData[1];
        shipments[0].deductibleUnderThePolicyCurrency = freightCurrencyData[0];
      } else {
        shipments[0].deductibleUnderThePolicyCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].totalValueOfLoss) {
        const freightCurrencyData = shipments[0].totalValueOfLoss.split(
          SEPARATOR
        );
        shipments[0].totalValueOfLoss = freightCurrencyData[1];
        shipments[0].totalValueOfLossCurrency = freightCurrencyData[0];
      } else {
        shipments[0].totalValueOfLossCurrency = CURRENCY_LIST[0].value;
      }

      if (shipments[0].tampering === null) {
        shipments[0].tampering = -1;
      }

      if (shipments[0].sealIntact === null) {
        shipments[0].sealIntact = -1;
      }

      if (shipments[0].packingIntact === null) {
        shipments[0].packingIntact = -1;
      }
    }

    if (qmus[0]) {
      if (typeof qmus[0].oer === "boolean") {
        res.data.cases.qmus[0].oer = Number(res.data.cases.qmus[0].oer);
      }

      if (typeof qmus[0].recurrent === "boolean") {
        res.data.cases.qmus[0].recurrent = Number(
          res.data.cases.qmus[0].recurrent
        );
      }

      if (qmus[0].centreToHandleComplaint) {
        let centreToHandleComplaint: any[] = [];
        qmus[0].centreToHandleComplaint.split(",").map((item: any) => {
          const center = solutionCentreList.filter(
            (x: any) => x.shortName == item.trim()
          );

          if (center.length) {
            const { shortName, fullName } = center[0];
            centreToHandleComplaint.push({
              id: shortName,
              name: `${fullName} (${shortName})`,
              label: `${fullName} (${shortName})`,
              value: shortName,
            });
          }
        });
        qmus[0].centreToHandleComplaintList = centreToHandleComplaint;
      }

      if (qmus[0].responsibility) {
        let respList: any[] = [];
        qmus[0].responsibility.split(",").map((item: any) => {
          const resp = responsibilityData.filter(
            (x: any) => x.id == item.trim()
          )[0];
          if (resp) {
            respList.push({
              id: resp.id,
              name: resp.name,
              label: resp.name,
              value: resp.id,
            });
          }
        });
        qmus[0].responsibilityList = respList;
      }

      if (qmus[0].solutionOwner) {
        const solutionOwner = qmus[0].solutionOwner
          .split(",")
          .map((item: any) => {
            let chipLabel = item;
            if (chipLabel.includes(":")) {
              chipLabel = getStringBeforeSubstring(chipLabel, ":");
            }
            return {
              label: item,
              value: item,
              chipLabel,
            };
          });
        qmus[0].solutionOwner = solutionOwner;
      }

      if (qmus[0].caSolutionOwner) {
        const caSolutionOwner = qmus[0].caSolutionOwner
          .split(",")
          .map((item: any) => {
            let chipLabel = item;
            if (chipLabel.includes(":")) {
              chipLabel = getStringBeforeSubstring(chipLabel, ":");
            }
            return {
              label: item,
              value: item,
              chipLabel,
            };
          });
        qmus[0].caSolutionOwner = caSolutionOwner;
      }

      if (qmus[0].solutionTeam) {
        const solutionTeam = qmus[0].solutionTeam
          .split(",")
          .map((item: any) => {
            let chipLabel = item;
            if (chipLabel.includes(":")) {
              chipLabel = getStringBeforeSubstring(chipLabel, ":");
            }
            return {
              label: item,
              value: item,
              chipLabel,
            };
          });
        qmus[0].solutionTeam = solutionTeam;
      }
    }

    // Logic to filter out different form attachments
    if (attachments.length) {
      // Attachment data - Main form
      const attachmentData = attachments.filter((data: any) => {
        return data.type === FORM_TYPE_MAIN;
      });
      yield put(actionSetAttachmentData(attachmentData));

      // Attachment data - Shipment
      const attachmentDataShipment = attachments.filter((data: any) => {
        return data.type === FORM_TYPE_SHIPMENT;
      });
      yield put(actionSetAttachmentDataShipment(attachmentDataShipment));

      // Attachment data - Shipment Inventory
      const attachmentDataShipmentInventory = attachments.filter(
        (data: any) => {
          return data.type === FORM_TYPE_SHIPMENT_INVENTORY;
        }
      );
      yield put(
        actionSetAttachmentDataShipmentInventory(
          attachmentDataShipmentInventory
        )
      );

      // Attachment data - QMU
      const attachmentDataQmu = attachments.filter((data: any) => {
        return data.type === FORM_TYPE_QMU;
      });
      yield put(actionSetAttachmentDataQmu(attachmentDataQmu));

      // Attachment data - Inventory
      const attachmentDataInventory = attachments.filter((data: any) => {
        return data.type === FORM_TYPE_INVENTORY;
      });
      yield put(actionSetAttachmentDataInventory(attachmentDataInventory));

      // Attachment data - QMU OER
      const attachmentDataQmuOer = attachments.filter((data: any) => {
        return data.type === FORM_TYPE_QMU_OER;
      });
      yield put(actionSetAttachmentDataQmuOer(attachmentDataQmuOer));
    }

    yield put(fetchCaseSucceeded(res.data.cases));

    // Set the current user username/email id
    const currentUser = state.home.currentUser;
    let currentUsername = currentUser ? currentUser.userName : "";
    let checkOutByUser = res.data.cases.checkOutBy;
    if (currentUsername) {
      if (currentUsername.includes(":")) {
        currentUsername = getStringAfterSubstring(currentUsername, ":").trim();
      } else {
        currentUsername = currentUsername.trim();
      }
      yield put(actionSetCurrentUsername(currentUsername));
    }

    // Set the value if the case is checkout by the same user or not.
    if (currentUsername && checkOutByUser) {
      if (checkOutByUser.includes(":")) {
        checkOutByUser = getStringAfterSubstring(checkOutByUser, ":").trim();
      } else {
        checkOutByUser = checkOutByUser.trim();
      }

      if (currentUsername === checkOutByUser) {
        yield put(actionIsCheckOutByDiffUser(false));
      } else {
        yield put(actionIsCheckOutByDiffUser(true));
      }
    }

    // Check if the logged in user is a SO/ST and the case is assigned to the current user
    if (qmus[0] && currentUser) {
      const currentRoleId = currentUser.roleId;
      if (
        currentRoleId === UserRoles.Solution_Owner ||
        currentRoleId === UserRoles.Solution_Team
      ) {
        let filteredSOUser = [];
        let filteredSTUser = [];

        if (qmus[0].solutionOwner) {
          filteredSOUser = qmus[0].solutionOwner.filter(
            (item: any) => item.value === currentUser.userName
          );
        }

        if (qmus[0].solutionTeam) {
          filteredSTUser = qmus[0].solutionTeam.filter(
            (item: any) => item.value === currentUser.userName
          );
        }

        if (filteredSOUser.length) {
          yield put(actionIsCurrentUserSO(true));
        } else {
          yield put(actionIsCurrentUserSO(false));
        }

        if (filteredSTUser.length) {
          yield put(actionIsCurrentUserST(true));
        } else {
          yield put(actionIsCurrentUserST(false));
        }
      }
    }
  } else {
    // TODO: Add generic error handling here
  }
}

function* saveCase(action: any) {
  const caseData: any = action.payload;
  const shipmentsTabData = caseData.shipments ? caseData.shipments[0] : null;
  const qmuData = caseData.qmus ? caseData.qmus[0] : null;
  const posData = caseData.pos ? caseData.pos : null;
  const state: IRootState = yield select();

  yield put(setLoaderVisibility(true));
  caseData.createdBy = state.home.currentUser ? caseData.createdBy : "";

  // if (!caseData.hasOwnProperty("countriesAffected")) {
  //   caseData.countriesAffected = COUNTRY_NOT_APPLICABLE;
  // }

  // Check what is the issue Type
  if (caseData.caseFormType !== CaseFormType.WarehouseNonConformity) {
    if (caseData.issueType !== IssueType.Shipment) {
      caseData.inCaseOfShipment = -1;
    }
  }
  caseData.countriesAffected = caseData.countriesAffected
    .map((item: any) => item.id)
    .toString();

  caseData.expectedLossValue = caseData.expectedLossValue
    ? `${caseData.expectedLossCurrency}${SEPARATOR}${caseData.expectedLossValue}`
    : caseData.expectedLossValue;
  if (caseData.insurancePolicyYear) {
    caseData.insurancePolicyYear = `${caseData.insurancePolicyYear}-01-01T00:00:00`;
  }
  if (shipmentsTabData) {
    shipmentsTabData.actualInsuredValues = shipmentsTabData.actualInsuredValues
      ? `${shipmentsTabData.actualInsuredValuesCurrency}${SEPARATOR}${shipmentsTabData.actualInsuredValues}`
      : shipmentsTabData.actualInsuredValues;

    shipmentsTabData.premium = shipmentsTabData.premium
      ? `${shipmentsTabData.premiumCurrency}${SEPARATOR}${shipmentsTabData.premium}`
      : shipmentsTabData.premium;

    shipmentsTabData.totalClaimsValuesSupplies = shipmentsTabData.totalClaimsValuesSupplies
      ? `${shipmentsTabData.totalClaimsValuesSuppliesCurrency}${SEPARATOR}${shipmentsTabData.totalClaimsValuesSupplies}`
      : shipmentsTabData.totalClaimsValuesSupplies;

    shipmentsTabData.claimsValueFreight = shipmentsTabData.claimsValueFreight
      ? `${shipmentsTabData.claimsValueFreightCurrency}${SEPARATOR}${shipmentsTabData.claimsValueFreight}`
      : shipmentsTabData.claimsValueFreight;

    shipmentsTabData.uplift = shipmentsTabData.uplift
      ? `${shipmentsTabData.upliftCurrency}${SEPARATOR}${shipmentsTabData.uplift}`
      : shipmentsTabData.uplift;

    shipmentsTabData.deductibleUnderThePolicy = shipmentsTabData.deductibleUnderThePolicy
      ? `${shipmentsTabData.deductibleUnderThePolicyCurrency}${SEPARATOR}${shipmentsTabData.deductibleUnderThePolicy}`
      : shipmentsTabData.deductibleUnderThePolicy;

    shipmentsTabData.totalValueOfLoss = shipmentsTabData.totalValueOfLoss
      ? `${shipmentsTabData.totalValueOfLossCurrency}${SEPARATOR}${shipmentsTabData.totalValueOfLoss}`
      : shipmentsTabData.totalValueOfLoss;

    // Check empty dates
    if (shipmentsTabData.dateReceivedInCountry) {
      shipmentsTabData.dateReceivedInCountry = emptyDate(
        shipmentsTabData.dateReceivedInCountry
      );
    }

    if (shipmentsTabData.customsClearanceDate) {
      shipmentsTabData.customsClearanceDate = emptyDate(
        shipmentsTabData.customsClearanceDate
      );
    }

    if (shipmentsTabData.bldate) {
      shipmentsTabData.bldate = emptyDate(shipmentsTabData.bldate);
    }

    if (shipmentsTabData.transportStart) {
      shipmentsTabData.transportStart = emptyDate(
        shipmentsTabData.transportStart
      );
    }

    if (shipmentsTabData.transportEnd) {
      shipmentsTabData.transportEnd = emptyDate(shipmentsTabData.transportEnd);
    }

    if (shipmentsTabData.reportedOn) {
      shipmentsTabData.reportedOn = emptyDate(shipmentsTabData.reportedOn);
    }

    if (shipmentsTabData.grantExpiry) {
      shipmentsTabData.grantExpiry = emptyDate(shipmentsTabData.grantExpiry);
    }

    if (shipmentsTabData.presentedForSettlement) {
      shipmentsTabData.presentedForSettlement = emptyDate(
        shipmentsTabData.presentedForSettlement
      );
    }

    if (shipmentsTabData.claimClosed) {
      shipmentsTabData.claimClosed = emptyDate(shipmentsTabData.claimClosed);
    }

    // Checking compulsory dropdown fields
    if (shipmentsTabData.tampering === -1) {
      shipmentsTabData.tampering = null;
    }

    if (shipmentsTabData.sealIntact === -1) {
      shipmentsTabData.sealIntact = null;
    }

    if (shipmentsTabData.packingIntact === -1) {
      shipmentsTabData.packingIntact = null;
    }
  }

  if (qmuData) {
    if (qmuData.solutionOwner) {
      if (qmuData.solutionOwner.length === 0) {
        qmuData.solutionOwner = "";
      } else {
        const solutionOwner = qmuData.solutionOwner
          .map((item: any) => item.value)
          .toString();
        qmuData.solutionOwner = solutionOwner;
      }
    }

    if (qmuData.caSolutionOwner) {
      if (qmuData.caSolutionOwner.length === 0) {
        qmuData.caSolutionOwner = "";
      } else {
        const caSolutionOwner = qmuData.caSolutionOwner
          .map((item: any) => item.value)
          .toString();
        qmuData.caSolutionOwner = caSolutionOwner;
      }
    }

    if (qmuData.solutionTeam) {
      if (qmuData.solutionTeam.length === 0) {
        qmuData.solutionTeam = "";
      } else {
        const solutionTeam = qmuData.solutionTeam
          .map((item: any) => item.value)
          .toString();
        qmuData.solutionTeam = solutionTeam;
      }
    }

    // Check empty dates
    if (qmuData.validatedDate) {
      qmuData.validatedDate = emptyDate(qmuData.validatedDate);
    }

    if (qmuData.correctionIdentified) {
      qmuData.correctionIdentified = emptyDate(qmuData.correctionIdentified);
    }

    if (qmuData.correctionCompleted) {
      qmuData.correctionCompleted = emptyDate(qmuData.correctionCompleted);
    }

    if (qmuData.correctiveActionIdentified) {
      qmuData.correctiveActionIdentified = emptyDate(
        qmuData.correctiveActionIdentified
      );
    }

    if (qmuData.correctiveActionCompleted) {
      qmuData.correctiveActionCompleted = emptyDate(
        qmuData.correctiveActionCompleted
      );
    }

    if (qmuData.beingDrafted) {
      qmuData.beingDrafted = emptyDate(qmuData.beingDrafted);
    }

    if (qmuData.sentDoforPreapproval) {
      qmuData.sentDoforPreapproval = emptyDate(qmuData.sentDoforPreapproval);
    }

    if (qmuData.preapprovedByDo) {
      qmuData.preapprovedByDo = emptyDate(qmuData.preapprovedByDo);
    }

    if (qmuData.circulatedForComments) {
      qmuData.circulatedForComments = emptyDate(qmuData.circulatedForComments);
    }

    if (qmuData.circulatedForSignatures) {
      qmuData.circulatedForSignatures = emptyDate(
        qmuData.circulatedForSignatures
      );
    }

    if (qmuData.signed) {
      qmuData.signed = emptyDate(qmuData.signed);
    }
  }

  if (posData) {
    caseData.pos = posData.map((value: any) => {
      if (value.hasOwnProperty("poNo") && !value.hasOwnProperty("poItemNo")) {
        value.poItemNo = "";
      }
      return value;
    });
  }

  const res = yield call(saveCaseUtility, caseData);

  if (res.success) {
    alert(res.message);
    yield put(saveCaseSucceeded());
  } else {
    yield put(saveCaseFailed(res.message));
  }
  yield put(setLoaderVisibility(false));
}

function* updateCase(action: any) {
  try {
    const modifiedCase: any = action.payload;
    const state: IRootState = yield select();
    const shipmentsTabData = modifiedCase.shipments[0];
    const qmuData = modifiedCase.qmus[0];
    const posData = modifiedCase.pos;
    if (modifiedCase.insurancePolicyYear) {
      modifiedCase.insurancePolicyYear = `${modifiedCase.insurancePolicyYear}-01-01T00:00:00`;
    }

    // if (!modifiedCase.countriesAffected) {
    //   modifiedCase.countriesAffected = COUNTRY_NOT_APPLICABLE;
    // }

    yield put(setLoaderVisibility(true));

    // Check what is the issue Type
    if (modifiedCase.caseFormType !== CaseFormType.WarehouseNonConformity) {
      if (modifiedCase.issueType !== IssueType.Shipment && modifiedCase.issueType <= IssueType.Pharmacovigilance) {
        modifiedCase.inCaseOfShipment = -1;
      }
    }

    modifiedCase.countriesAffected = modifiedCase.countriesAffected
      .map((item: any) => item.id)
      .toString();

    modifiedCase.expectedLossValue = modifiedCase.expectedLossValue
      ? `${modifiedCase.expectedLossCurrency}${SEPARATOR}${modifiedCase.expectedLossValue}`
      : modifiedCase.expectedLossValue;

    if (shipmentsTabData) {
      shipmentsTabData.actualInsuredValues = shipmentsTabData.actualInsuredValues
        ? `${shipmentsTabData.actualInsuredValuesCurrency}${SEPARATOR}${shipmentsTabData.actualInsuredValues}`
        : shipmentsTabData.actualInsuredValues;

      shipmentsTabData.premium = shipmentsTabData.premium
        ? `${shipmentsTabData.premiumCurrency}${SEPARATOR}${shipmentsTabData.premium}`
        : shipmentsTabData.premium;

      shipmentsTabData.totalClaimsValuesSupplies = shipmentsTabData.totalClaimsValuesSupplies
        ? `${shipmentsTabData.totalClaimsValuesSuppliesCurrency}${SEPARATOR}${shipmentsTabData.totalClaimsValuesSupplies}`
        : shipmentsTabData.totalClaimsValuesSupplies;

      shipmentsTabData.claimsValueFreight = shipmentsTabData.claimsValueFreight
        ? `${shipmentsTabData.claimsValueFreightCurrency}${SEPARATOR}${shipmentsTabData.claimsValueFreight}`
        : shipmentsTabData.claimsValueFreight;

      shipmentsTabData.uplift = shipmentsTabData.uplift
        ? `${shipmentsTabData.upliftCurrency}${SEPARATOR}${shipmentsTabData.uplift}`
        : shipmentsTabData.uplift;

      shipmentsTabData.deductibleUnderThePolicy = shipmentsTabData.deductibleUnderThePolicy
        ? `${shipmentsTabData.deductibleUnderThePolicyCurrency}${SEPARATOR}${shipmentsTabData.deductibleUnderThePolicy}`
        : shipmentsTabData.deductibleUnderThePolicy;

      shipmentsTabData.totalValueOfLoss = shipmentsTabData.totalValueOfLoss
        ? `${shipmentsTabData.totalValueOfLossCurrency}${SEPARATOR}${shipmentsTabData.totalValueOfLoss}`
        : shipmentsTabData.totalValueOfLoss;

      // Check empty dates
      if (shipmentsTabData.dateReceivedInCountry) {
        shipmentsTabData.dateReceivedInCountry = emptyDate(
          shipmentsTabData.dateReceivedInCountry
        );
      }

      if (shipmentsTabData.customsClearanceDate) {
        shipmentsTabData.customsClearanceDate = emptyDate(
          shipmentsTabData.customsClearanceDate
        );
      }

      if (shipmentsTabData.bldate) {
        shipmentsTabData.bldate = emptyDate(shipmentsTabData.bldate);
      }

      if (shipmentsTabData.transportStart) {
        shipmentsTabData.transportStart = emptyDate(
          shipmentsTabData.transportStart
        );
      }

      if (shipmentsTabData.transportEnd) {
        shipmentsTabData.transportEnd = emptyDate(
          shipmentsTabData.transportEnd
        );
      }

      if (shipmentsTabData.reportedOn) {
        shipmentsTabData.reportedOn = emptyDate(shipmentsTabData.reportedOn);
      }

      if (shipmentsTabData.grantExpiry) {
        shipmentsTabData.grantExpiry = emptyDate(shipmentsTabData.grantExpiry);
      }

      if (shipmentsTabData.presentedForSettlement) {
        shipmentsTabData.presentedForSettlement = emptyDate(
          shipmentsTabData.presentedForSettlement
        );
      }

      if (shipmentsTabData.claimClosed) {
        shipmentsTabData.claimClosed = emptyDate(shipmentsTabData.claimClosed);
      }

      if (shipmentsTabData.tampering === -1) {
        shipmentsTabData.tampering = null;
      }

      if (shipmentsTabData.sealIntact === -1) {
        shipmentsTabData.sealIntact = null;
      }

      if (shipmentsTabData.packingIntact === -1) {
        shipmentsTabData.packingIntact = null;
      }
    }

    if (qmuData) {
      if (qmuData.solutionOwner) {
        if (qmuData.solutionOwner.length === 0) {
          qmuData.solutionOwner = "";
        } else {
          const solutionOwner = qmuData.solutionOwner
            .map((item: any) => item.value)
            .toString();
          qmuData.solutionOwner = solutionOwner;
        }
      }

      if (qmuData.caSolutionOwner) {
        if (qmuData.caSolutionOwner.length === 0) {
          qmuData.caSolutionOwner = "";
        } else {
          const caSolutionOwner = qmuData.caSolutionOwner
            .map((item: any) => item.value)
            .toString();
          qmuData.caSolutionOwner = caSolutionOwner;
        }
      }

      if (qmuData.solutionTeam) {
        if (qmuData.solutionTeam.length === 0) {
          qmuData.solutionTeam = "";
        } else {
          const solutionTeam = qmuData.solutionTeam
            .map((item: any) => item.value)
            .toString();
          qmuData.solutionTeam = solutionTeam;
        }
      }

      // Check empty dates
      if (qmuData.validatedDate) {
        qmuData.validatedDate = emptyDate(qmuData.validatedDate);
      }

      if (qmuData.correctionIdentified) {
        qmuData.correctionIdentified = emptyDate(qmuData.correctionIdentified);
      }

      if (qmuData.correctionCompleted) {
        qmuData.correctionCompleted = emptyDate(qmuData.correctionCompleted);
      }

      if (qmuData.correctiveActionIdentified) {
        qmuData.correctiveActionIdentified = emptyDate(
          qmuData.correctiveActionIdentified
        );
      }

      if (qmuData.correctiveActionCompleted) {
        qmuData.correctiveActionCompleted = emptyDate(
          qmuData.correctiveActionCompleted
        );
      }

      if (qmuData.beingDrafted) {
        qmuData.beingDrafted = emptyDate(qmuData.beingDrafted);
      }

      if (qmuData.sentDoforPreapproval) {
        qmuData.sentDoforPreapproval = emptyDate(qmuData.sentDoforPreapproval);
      }

      if (qmuData.preapprovedByDo) {
        qmuData.preapprovedByDo = emptyDate(qmuData.preapprovedByDo);
      }

      if (qmuData.circulatedForComments) {
        qmuData.circulatedForComments = emptyDate(
          qmuData.circulatedForComments
        );
      }

      if (qmuData.circulatedForSignatures) {
        qmuData.circulatedForSignatures = emptyDate(
          qmuData.circulatedForSignatures
        );
      }

      if (qmuData.signed) {
        qmuData.signed = emptyDate(qmuData.signed);
      }
    }

    if (action.payload.isReopen) {
      if (
        modifiedCase.caseType === CaseType.Complaint &&
        modifiedCase.complaintStatus === false
      ) {
        modifiedCase.complaintStatus = true;
      } else if (
        modifiedCase.caseType === CaseType.Claim &&
        modifiedCase.claimStatus === ClaimStatus.Closed
      ) {
        modifiedCase.claimStatus = ClaimStatus.Open;
      } else if (
        modifiedCase.caseType === CaseType.ComplaintClaim &&
        modifiedCase.claimStatus === ClaimStatus.Closed &&
        modifiedCase.complaintStatus === false
      ) {
        if (state.home.currentUser!.roleId === UserRoles.INS) {
          modifiedCase.claimStatus = ClaimStatus.Open;
        } else if (state.home.currentUser!.roleId === UserRoles.QMU) {
          modifiedCase.complaintStatus = true;
        }
      }
    } else if (
      modifiedCase.qmus[0] &&
      modifiedCase.qmus[0].correctionIdentified &&
      modifiedCase.qmus[0].correctiveActionIdentified &&
      modifiedCase.qmus[0].correctionCompleted &&
      modifiedCase.qmus[0].correctiveActionCompleted &&
      state.home.currentUser!.roleId === UserRoles.QMU
    ) {
      const closeCase = window.confirm(
        "Are you sure you want to close this case? "
      );
      if (closeCase) {
        modifiedCase.complaintStatus = false;
      }
    }

    if (
      posData.length > 1 ||
      (posData[0] && posData[0].hasOwnProperty("poNo"))
    ) {
      // @ts-ignore
      modifiedCase.pos = posData.map((value: any) => {
        if (value.hasOwnProperty("poNo") && !value.hasOwnProperty("poItemNo")) {
          value.poItemNo = "";
        }
        return value;
      });
    }

    // Get the current state.
    const res = yield call(updateCaseUtility, {
      ...modifiedCase,
      modifiedBy: state.home.currentUser!.userName,
    }); // Overwrite this specific field with new value.
    // TODO: remove before production

    yield put(setLoaderVisibility(false));
    if (res.success) {
      if (action.payload.isReopen) {
        alert(REOPEN_MESSAGE);
        window.location.reload(false);
      } else {
        if (
          state.home.currentUser!.roleId === UserRoles.Solution_Owner ||
          state.home.currentUser!.roleId === UserRoles.Solution_Team
        ) {
          alert(DATA_SAVE_SUCCESS);
          window.location.reload(false);
        } else {
          alert(res.message);
          yield put(saveCaseSucceeded());
        }
      }
    } else {
      yield put(saveCaseFailed(res.message));
    }
  } catch (e) {
    yield put(saveCaseFailed(e));
  }
}

function* watchSelectCase() {
  yield takeLatest(ActionTypes.DETAIL.SELECT_CASE, selectCase);
}

function* selectCase(action: any) {
  try {
    yield put(newCase());
    yield call(
      [history, "push"],
      [DETAIL_PATH, action.payload, "mainform"].join("/")
    );
  } catch {
    console.log("Couldn't select case", action.payload);
  }
}

function* saveAttachment(action: any) {
  try {
    yield put(setLoaderVisibility(true));

    const response = yield call(saveAttachmentDataUtility, action.payload);

    if (response.data.success) {
      // set attachment data
      let attachmentData: any = [];
      if (action.payload.prevAttachmentData) {
        attachmentData = JSON.parse(
          JSON.stringify(action.payload.prevAttachmentData)
        );
      }

      response.data.data.forEach((item: any) => {
        attachmentData.push(item);
      });

      // switch (action.payload.type) {
      //   case "mainform":
      //     yield put(actionSetAttachmentData(attachmentData));
      //     break;
      //   case "shipment":
      //     yield put(actionSetAttachmentDataShipment(attachmentData));
      //     break;
      //   default:
      //     break;
      // }

      yield setAttachmentData(action.payload.type, attachmentData);
    }
    yield put(setLoaderVisibility(false));
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

function* fileDownload(data: any) {
  try {
    const response = yield call(downloadAttachmentUtility, data.payload.fileId);
    const bytes = base64ToArrayBuffer(response.data.attachmentDataStream);

    setTimeout(() => {
      const file: any = new Blob([bytes], { type: "application/octet-stream" });
      const a = document.createElement("a");
      a.className = "link-download";
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(file);
      a.href = url;
      a.download = response.data.fileName;
      a.click();
      document.body.removeChild(a);
    }, 0);
  } catch (err) {
    yield put(openToaster(FILE_DOWNLOAD_ERROR, false));
  }
}

function* fileDelete(action: any) {
  try {
    yield put(setLoaderVisibility(true));

    const response = yield call(deleteAttachmentUtility, action.payload.fileId);

    if (response.success) {
      // update attachment data
      const attachmentData = JSON.parse(
        JSON.stringify(action.payload.attachmentData)
      );
      const data = attachmentData.filter((item: any) => {
        return item.id !== action.payload.fileId;
      });

      // yield put(actionSetAttachmentData(data));
      yield setAttachmentData(action.payload.type, data);
    }
    yield put(setLoaderVisibility(false));
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

function* setAttachmentData(type: string, attachmentData: any) {
  switch (type) {
    case FORM_TYPE_MAIN:
      yield put(actionSetAttachmentData(attachmentData));
      break;
    case FORM_TYPE_SHIPMENT:
      yield put(actionSetAttachmentDataShipment(attachmentData));
      break;
    case FORM_TYPE_SHIPMENT_INVENTORY:
      yield put(actionSetAttachmentDataShipmentInventory(attachmentData));
      break;
    case FORM_TYPE_QMU:
      yield put(actionSetAttachmentDataQmu(attachmentData));
      break;
    case FORM_TYPE_INVENTORY:
      yield put(actionSetAttachmentDataInventory(attachmentData));
      break;
    case FORM_TYPE_QMU_OER:
      yield put(actionSetAttachmentDataQmuOer(attachmentData));
      break;
    default:
      break;
  }
}

function* fnCaseLockUnlock(action: any) {
  try {
    const state: IRootState = yield select();
    let username = action.payload.username;

    if (username === -1) {
      yield put(actionAllowFetchGridData(false));
    }

    // -2 is a flag set to identify user has clicked on Unlock Case button
    if (username === -2) {
      username = -1;
    }
    yield put(setLoaderVisibility(true));
    const response = yield call(
      caseLockUnlockUtility,
      action.payload.uniqueId,
      username
    );
    if (response.success) {
      if (action.payload.username === -2) {
        yield put(allowEditCase(false));
        const caseData: any = state.detail.caseData;
        if (caseData && caseData.checkOutBy) {
          caseData.checkOutBy = "";
        }
        yield put(actionIsCheckOutByDiffUser(false));
        yield put(fetchCaseSucceeded(caseData));
      } else {
        yield put(allowEditCase(true));
        if (username === -1) {
          yield put(actionAllowFetchGridData(true));
        }
      }
    } else {
      alert(response.message);
      yield put(allowEditCase(false));
    }
    yield put(setLoaderVisibility(false));
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

function* fnCaseLockForEdit(action: any) {
  try {
    yield put(setLoaderVisibility(true));
    const response = yield call(
      caseLockForEditUtility,
      action.payload.uniqueId,
      action.payload.isEdit,
      action.payload.updatedBy
    );
    if (response.success) {
      // refresh the page
      yield put(isLockForEdit(true));
    }
    yield put(setLoaderVisibility(false));
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

function* fnConfirmDialog(action: any) {
  const state: IRootState = yield select();
  const { key } = state.common.confirmDialog;
  const caseData = state.detail.caseData;
  const currentUser = state.home.currentUser;
  switch (key) {
    case CDK_APPROVE:
      // @ts-ignore
      yield put(caseLockForEdit(caseData.uniqueId, true, currentUser.userName));
      yield put(closeConfirmDialog());
      break;
    case CDK_CLOSE_BTN:
      // @ts-ignore
      yield put(newCase());
      yield put(navigateHome());
      yield put(closeConfirmDialog());
      break;
    default:
      yield put(closeConfirmDialog());
      break;
  }
}

function* fnRequestForClose(action: any) {
  try {
    yield put(setLoaderVisibility(true));
    const { caseData, userDetails } = action.payload;
    if (caseData.qmus && caseData.qmus.length) {
      if (caseData.qmus[0].recurrent === null) {
        alert(
          "Please update case with recurrent on complaint solutions tab then sent request for closure"
        );
        yield put(setLoaderVisibility(false));
      } else {
        const response = yield call(
          requestForCloseUtility,
          caseData,
          userDetails
        );
        if (response.success) {
          yield put(setLoaderVisibility(false));
          alert(REQUEST_FOR_CLOSE_MESSAGE);
          yield put(navigateHome());
        }
      }
    }
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

function* watchSaveSPUser() {
  yield takeLatest(ActionTypes.DETAIL.SAVE_SP_USER, fnSaveSPUser);
}

function* fnSaveSPUser(action: any) {
  try {
    yield put(setLoaderVisibility(true));
    const value = action.payload;
    const userDetailsList = {
      userDetailsList: [
        {
          ...value,
          role: "SolutionOwner",
        },
      ],
    };
    const response = yield call(apiSaveSPUserData, userDetailsList);
    const { success, message } = response.data;
    yield put(setLoaderVisibility(false));
    if (success) {
      yield put(openToaster(message, true));
      yield delay(2000);
      yield put(closeToaster());
      yield put(reset("solutionProviderForm"));
    } else {
      yield put(openToaster(message, false));
    }
  } catch (err) {
    console.log(err);
    yield put(setLoaderVisibility(false));
    yield put(openToaster(API_ERROR_MESSAGE, false));
  }
}

export default function* detailSaga() {
  yield all([
    watchUpdateCase(),
    watchSelectCase(),
    watchSaveCase(),
    watchCaseSaveSucceded(),
    watchFetchCase(),
    watchNavigateHome(),
    watchSaveAttachment(),
    watchFileDownload(),
    watchFileDelete(),
    watchNavigatePrint(),
    watchCaseLockUnlock(),
    watchCaseLockForEdit(),
    watchConfirmDialog(),
    watchRequestForClose(),
    watchSaveSPUser(),
  ]);
}
